import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner } from '../components';
import { paymentsAPI, projectsAPI } from '../services/api';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [captureDone, setCaptureDone] = useState(false);
  const [captureData, setCaptureData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [startedFrom, setStartedFrom] = useState('/create-avatar');
  const [isPopup, setIsPopup] = useState(false);

  const orderId = useMemo(() => searchParams.get('token') || '', [searchParams]);

  useEffect(() => {
    // Remove query params from URL to prevent re-running capture on refresh/back
    if (orderId && window?.history?.replaceState) {
      window.history.replaceState({}, '', '/payment/success');
    }
  }, [orderId]);

  useEffect(() => {
    const runCapture = async () => {
      if (!orderId) {
        toast.error('Missing PayPal order token.');
        navigate('/create-avatar', { replace: true });
        return;
      }

      // Check for authentication - capture is a protected action
      const token = localStorage.getItem('access_token');
      if (!token) {
        toast.error('Session expired or access denied. Please sign in to finalize your payment.');
        navigate('/signin', {
          state: { from: location.pathname + location.search },
          replace: true
        });
        return;
      }

      const rawCtx = sessionStorage.getItem('paypal_checkout_context') || localStorage.getItem('paypal_checkout_context');
      if (!rawCtx) {
        try {
          const lookup = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
          if (lookup?.data) {
            setInvoiceData(lookup.data);
            setCaptureDone(true);
            setLoading(false);
            return;
          }
        } catch (_) { }

        toast.error('Payment session expired. Please contact support if symptoms persist.');
        navigate('/create-avatar', { replace: true });
        return;
      }

      let ctx = null;
      try {
        ctx = JSON.parse(rawCtx);
      } catch (_) {
        toast.error('Invalid checkout context. Please restart payment.');
        navigate('/create-avatar', { replace: true });
        return;
      }

      if (ctx?.started_from) {
        setStartedFrom(ctx.started_from);
      }

      if (ctx?.context === 'popup') {
        setIsPopup(true);
      }

      const avatarPlanId = ctx?.avatar_plan_id;
      const planInterval = ctx?.plan_interval;
      const autoRenew = Boolean(ctx?.auto_renew);

      if (!avatarPlanId || !planInterval) {
        toast.error('Checkout data is incomplete. Please restart payment.');
        navigate('/create-avatar', { replace: true });
        return;
      }

      const processedKey = `payment_processed_${orderId}`;
      const processingKey = `payment_processing_${orderId}`;

      // If already processed in this browser session, avoid duplicate capture call
      const alreadyProcessed = sessionStorage.getItem(processedKey) === 'true';
      if (alreadyProcessed) {
        // Try to restore from cached capture result
        const cached = sessionStorage.getItem('paypal_capture_result');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed?.order_id === orderId) {
              setCaptureData(parsed);
              setCaptureDone(true);
            }
          } catch (_) { }
        }
        // If no cached data, attempt invoice lookup as a lightweight recovery
        if (!captureDone) {
          try {
            const lookup = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
            const lookupData = lookup?.data || lookup;
            if (lookupData?.invoice_id) {
              const normalized = {
                order_id: orderId,
                avatar_plan_id: Number(avatarPlanId),
                plan_interval: String(planInterval),
                auto_renew: autoRenew,
                subscription_id: lookupData?.subscription_id || null,
                invoice_id: lookupData?.invoice_id || null,
                invoice_download_url: lookupData?.invoice_download_url || null,
              };
              setCaptureData(normalized);
              setCaptureDone(true);
              sessionStorage.setItem('paypal_capture_result', JSON.stringify(normalized));
              setInvoiceData(lookupData);
            }
          } catch (_) {
            // Ignore; user can retry manually from UI
          }
        }
        setLoading(false);
        return;
      }

      // Guard against duplicate effect execution (e.g. React StrictMode in dev).
      if (sessionStorage.getItem(processingKey) === 'true') {
        setLoading(false);
        return;
      }
      sessionStorage.setItem(processingKey, 'true');

      try {
        const captureRes = await paymentsAPI.capturePaypalOrder({
          order_id: orderId,
          avatar_plan_id: avatarPlanId,
          plan_interval: planInterval,
          auto_renew: autoRenew,
        });

        const payload = captureRes?.data || captureRes;
        const subscriptionId = payload?.subscription_id;
        const invoiceId = payload?.invoice_id;
        const invoiceDownloadUrl = payload?.invoice_download_url;

        const normalized = {
          order_id: orderId,
          avatar_plan_id: Number(avatarPlanId),
          plan_interval: String(planInterval),
          auto_renew: autoRenew,
          subscription_id: subscriptionId || null,
          invoice_id: invoiceId || null,
          invoice_download_url: invoiceDownloadUrl || null,
        };

        setCaptureData(normalized);
        setCaptureDone(true);
        sessionStorage.setItem('paypal_capture_result', JSON.stringify(normalized));
        sessionStorage.setItem(processedKey, 'true');

        // If this payment was for "I'll pay" family invite, call send-invitation with i_will_pay: 1
        const pendingInvite = ctx?.pending_invite;
        if (pendingInvite?.invite_email) {
          try {
            await projectsAPI.sendInvitation({
              invite_email: pendingInvite.invite_email,
              plan_id: pendingInvite.plan_pricing_id,
              plan_pricing_id: pendingInvite.plan_id,
              i_will_pay: 1,
            });
            // Clear pending_invite from stored context so it is not reused (check both storages)
            [sessionStorage, localStorage].forEach((storage) => {
              const raw = storage.getItem('paypal_checkout_context');
              if (raw) {
                try {
                  const updated = JSON.parse(raw);
                  delete updated.pending_invite;
                  storage.setItem('paypal_checkout_context', JSON.stringify(updated));
                } catch (_) { }
              }
            });
          } catch (inviteErr) {
            console.error('Send invitation after payment failed:', inviteErr);
            toast.error(inviteErr?.message || 'Payment succeeded but sending the invitation failed. Please contact support.');
          }
        }

        // Fetch detailed invoice information
        try {
          const invoiceResponse = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
          if (invoiceResponse?.data) {
            setInvoiceData(invoiceResponse.data);
          }
        } catch (invoiceError) {
          console.warn('Failed to fetch invoice details:', invoiceError);
          // Don't show error toast for invoice fetch failure as it's not critical
        }
      } catch (error) {
        console.error('Capture failed:', error);
        // Optional restore helper: invoice lookup by paypal order id.
        try {
          const lookup = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
          const lookupData = lookup?.data || lookup;
          if (lookupData?.invoice_id) {
            const normalized = {
              order_id: orderId,
              avatar_plan_id: Number(avatarPlanId),
              plan_interval: String(planInterval),
              auto_renew: autoRenew,
              subscription_id: lookupData?.subscription_id || null,
              invoice_id: lookupData?.invoice_id || null,
              invoice_download_url: lookupData?.invoice_download_url || null,
            };
            setCaptureData(normalized);
            setCaptureDone(true);
            sessionStorage.setItem('paypal_capture_result', JSON.stringify(normalized));
            sessionStorage.setItem(processedKey, 'true');
            setInvoiceData(lookupData); // Set invoice data from recovery
            toast.info('Recovered invoice details from existing PayPal order.');
          } else {
            throw error;
          }
        } catch (_) {
          toast.error(error?.message || 'Payment capture failed. Please try again.');
        }
      } finally {
        sessionStorage.removeItem(processingKey);
        setLoading(false);
      }
    };

    runCapture();
  }, [navigate, orderId]);

  useEffect(() => {
    let timer;
    // When loading is finished, check if we're in a popup context
    if (!loading) {
      // Determine if we should treat this as a popup (either by state, opener, or window name)
      const shouldClose = isPopup || (window.opener && window.opener !== window) || window.name === 'PayPal';

      if (shouldClose) {
        try {
          // Send signal to parent (success or failure)
          if (window.opener) {
            window.opener.postMessage({
              type: captureDone ? 'PAYPAL_SUCCESS' : 'PAYPAL_ERROR',
              data: captureData,
              error: captureDone ? null : 'Payment confirmation failed' // Using original error message logic
            }, '*');

            console.log('PaymentSuccess: Posted message to opener, closing window in 500ms');
          }

          // Small delay to ensure message is sent then close
          setTimeout(() => {
            window.close();
          }, 500);
          return; // Keep the return here to prevent further execution in this effect for popups
        } catch (e) {
          console.error('Failed to close popup:', e);
          // Fallback redirect if close fails (only if not successful, and if startedFrom is available)
          if (!captureDone && startedFrom) { // Use captureDone and startedFrom from state
            navigate(startedFrom, { replace: true });
          }
        }
      }

      // If not a popup (or if close failed), proceed with immediate redirect on success
      if (captureDone) {
        navigate(startedFrom, { replace: true });
      }
    }
    return () => clearInterval(timer);
  }, [captureDone, loading, navigate, startedFrom, captureData, orderId, isPopup]);



  // When successful or loading, show the spinner instead of flashing the success UI
  if (loading || captureDone) {
    return (
      <div className="create-avatar-page">
        <Navbar disabled={true} />
        <LoadingSpinner fullScreen text={captureDone ? "Redirecting..." : "Finalizing your payment..."} />
      </div>
    );
  }

  return (
    <div className="create-avatar-page">
      <Navbar disabled={true} />
      <Container className="upload-container">
        <div className="upload-header">
          <h1>{captureDone ? 'Subscription Activated' : 'Payment Status'}</h1>
        </div>
        <div className="upload-form-wrapper">
          <div className="form-section">
            <p>
              {captureDone
                ? 'Your payment has been verified by backend capture. Redirecting...'
                : 'We could not confirm payment capture. Please retry or contact support.'}
            </p>
            <p><strong>Order ID:</strong> {orderId || 'N/A'}</p>
            <p><strong>Plan Interval:</strong> {captureData?.plan_interval || 'N/A'}</p>
            <p><strong>Auto Renew:</strong> <span style={{ textTransform: 'capitalize' }}>{captureData?.auto_renew ? 'enable' : 'disable'}</span></p>
            {captureData?.subscription_id && <p><strong>Subscription ID:</strong> {captureData.subscription_id}</p>}
            {invoiceData?.user_name && <p><strong>User Name:</strong> {invoiceData.user_name}</p>}
            {invoiceData?.payment_amount_usd && <p><strong>Payment Amount:</strong> ${invoiceData.payment_amount_usd} USD</p>}
          </div>

          <div className="modal-actions" style={{ marginTop: 16 }}>
            <CustomButton
              type="button"
              variant="secondary"
              onClick={() => {
                if (isPopup || (window.opener && window.opener !== window) || window.name === 'PayPal') {
                  window.close();
                } else {
                  navigate(startedFrom, { replace: true });
                }
              }}
            >
              {startedFrom.includes('/avatar/') ? 'Back to Profile' : 'Back to Create Avatar'}
            </CustomButton>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PaymentSuccessPage;


